
import React from "react";
import { Plus, Minus, Trash2, ShoppingBag, ArrowRight, ShieldCheck, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../../context/CartContext";

const formatPrice = (price) =>
  `${(Number(price) || 0).toLocaleString("fr-MA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} MAD`;

export default function CartPage() {
  const { items, total, updateQuantity, removeFromCart } = useCart();

  // Panier vide
  if (items.length === 0) {
    return (
      <main className="min-h-[70vh] bg-[#FAF9F6] px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 border-b border-[#E8E2D8] pb-6">
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#B58A45]">
              Votre sélection
            </p>
            <h1 className="font-display text-3xl font-semibold text-[#211E19] sm:text-4xl">
              Mon panier
            </h1>
          </div>

          <div className="mx-auto flex max-w-xl flex-col items-center rounded-3xl border border-[#E8E2D8] bg-white px-6 py-14 text-center shadow-[0_12px_40px_rgba(40,30,10,0.04)] sm:px-10">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#F5EFE3] text-[#B58A45]">
              <ShoppingBag size={32} strokeWidth={1.4} />
            </div>

            <h2 className="mt-7 font-display text-2xl font-semibold text-[#211E19]">
              Votre panier est vide
            </h2>

            <p className="mt-3 max-w-sm text-sm leading-7 text-[#777168]">
              Explorez notre collection et découvrez les produits qui vous plaisent.
            </p>

            <Link
              to="/"
              className="mt-8 inline-flex items-center justify-center gap-3 rounded-full bg-[#B58A45] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#B58A45]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#967039]"
            >
              Continuer mes achats
              <ArrowRight size={17} />
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAF9F6] px-4 py-10 sm:px-6 lg:px-8 lg:py-16">
      <div className="mx-auto max-w-7xl">

        {/* En-tête */}
        <header className="mb-8 flex flex-col justify-between gap-4 border-b border-[#E8E2D8] pb-6 sm:flex-row sm:items-end">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#B58A45]">
              Votre sélection
            </p>
            <h1 className="font-display text-3xl font-semibold text-[#211E19] sm:text-4xl">
              Mon panier
              <span className="ml-3 align-middle font-sans text-base font-normal text-[#8C857A]">
                ({items.length} {items.length > 1 ? "articles" : "article"})
              </span>
            </h1>
          </div>

          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#777168] transition-colors hover:text-[#B58A45]"
          >
            <ArrowRight size={16} className="rotate-180" />
            Continuer mes achats
          </Link>
        </header>

        {/* Contenu */}
        <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[minmax(0,1fr)_360px] xl:gap-10">

          {/* Liste des produits */}
          <section className="min-w-0">
            {/* Tableau desktop */}
            <div className="hidden overflow-hidden rounded-2xl border border-[#E8E2D8] bg-white shadow-[0_8px_30px_rgba(40,30,10,0.03)] md:block">
              <table className="w-full table-fixed text-left">
                <thead>
                  <tr className="border-b border-[#EEE9E0] bg-[#FDFCF9]">
                    <th className="w-[38%] px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8C857A] lg:px-6">
                      Produit
                    </th>
                    <th className="w-[17%] px-3 py-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8C857A]">
                      Prix
                    </th>
                    <th className="w-[22%] px-3 py-4 text-center text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8C857A]">
                      Quantité
                    </th>
                    <th className="w-[17%] px-3 py-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8C857A]">
                      Total
                    </th>
                    <th className="w-[6%] px-3 py-4" aria-label="Actions" />
                  </tr>
                </thead>

                <tbody className="divide-y divide-[#EEE9E0]">
                  {items.map((item) => {
                    const quantity = item.quantity || 1;
                    const price = Number(item.price) || 0;

                    return (
                      <tr
                        key={item.productId}
                        className="transition-colors hover:bg-[#FDFCF9]"
                      >
                        <td className="px-5 py-5 lg:px-6">
                          <div className="flex min-w-0 items-center gap-4">
                            <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl bg-[#F5F0E7]">
                              <img
                                src={item.image || "https://placehold.co/100x100?text=Produit"}
                                alt={item.name}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src = "https://placehold.co/100x100?text=Produit";
                                }}
                                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                              />
                            </div>

                            <p className="line-clamp-2 text-sm font-semibold leading-5 text-[#211E19]">
                              {item.name}
                            </p>
                          </div>
                        </td>

                        <td className="px-3 py-5 text-sm font-medium text-[#777168]">
                          {formatPrice(price)}
                        </td>

                        <td className="px-3 py-5">
                          <div className="mx-auto flex w-fit items-center gap-3 rounded-full border border-[#E8E2D8] bg-white p-1">
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(
                                  item.productId,
                                  Math.max(1, quantity - 1)
                                )
                              }
                              disabled={quantity <= 1}
                              aria-label={`Diminuer la quantité de ${item.name}`}
                              className="flex h-7 w-7 items-center justify-center rounded-full text-[#777168] transition-colors hover:bg-[#F5EFE3] hover:text-[#B58A45] disabled:cursor-not-allowed disabled:opacity-35"
                            >
                              <Minus size={13} />
                            </button>

                            <span className="min-w-4 text-center text-sm font-semibold text-[#211E19]">
                              {quantity}
                            </span>

                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(item.productId, quantity + 1)
                              }
                              aria-label={`Augmenter la quantité de ${item.name}`}
                              className="flex h-7 w-7 items-center justify-center rounded-full text-[#777168] transition-colors hover:bg-[#F5EFE3] hover:text-[#B58A45]"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                        </td>

                        <td className="px-3 py-5 text-sm font-semibold text-[#211E19]">
                          {formatPrice(price * quantity)}
                        </td>

                        <td className="px-3 py-5 text-center">
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.productId)}
                            aria-label={`Supprimer ${item.name} du panier`}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-[#A19A90] transition-all hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={16} strokeWidth={1.7} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Cartes mobile */}
            <div className="space-y-4 md:hidden">
              {items.map((item) => {
                const quantity = item.quantity || 1;
                const price = Number(item.price) || 0;

                return (
                  <article
                    key={item.productId}
                    className="rounded-2xl border border-[#E8E2D8] bg-white p-4 shadow-[0_6px_20px_rgba(40,30,10,0.03)]"
                  >
                    <div className="flex gap-4">
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-[#F5F0E7]">
                        <img
                          src={item.image || "https://placehold.co/120x120?text=Produit"}
                          alt={item.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "https://placehold.co/120x120?text=Produit";
                          }}
                          className="h-full w-full object-cover"
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h2 className="line-clamp-2 text-sm font-semibold leading-5 text-[#211E19]">
                          {item.name}
                        </h2>
                        <p className="mt-2 text-sm text-[#777168]">
                          {formatPrice(price)} / unité
                        </p>
                        <p className="mt-1 font-semibold text-[#B58A45]">
                          {formatPrice(price * quantity)}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeFromCart(item.productId)}
                        aria-label={`Supprimer ${item.name} du panier`}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#A19A90] transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="mt-4 flex items-center justify-between border-t border-[#EEE9E0] pt-4">
                      <span className="text-xs font-medium text-[#8C857A]">
                        Quantité
                      </span>

                      <div className="flex items-center gap-3 rounded-full border border-[#E8E2D8] p-1">
                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(
                              item.productId,
                              Math.max(1, quantity - 1)
                            )
                          }
                          disabled={quantity <= 1}
                          aria-label={`Diminuer la quantité de ${item.name}`}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-[#777168] hover:bg-[#F5EFE3] hover:text-[#B58A45] disabled:opacity-35"
                        >
                          <Minus size={14} />
                        </button>

                        <span className="min-w-4 text-center text-sm font-semibold text-[#211E19]">
                          {quantity}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            updateQuantity(item.productId, quantity + 1)
                          }
                          aria-label={`Augmenter la quantité de ${item.name}`}
                          className="flex h-8 w-8 items-center justify-center rounded-full text-[#777168] hover:bg-[#F5EFE3] hover:text-[#B58A45]"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {/* Continuer les achats */}
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#777168] transition-colors hover:text-[#B58A45]"
            >
              <ArrowRight size={16} className="rotate-180" />
              Retourner à la boutique
            </Link>
          </section>

          {/* Récapitulatif */}
          <aside className="lg:sticky lg:top-28">
            <div className="overflow-hidden rounded-2xl border border-[#E8E2D8] bg-white shadow-[0_12px_35px_rgba(40,30,10,0.05)]">
              <div className="border-b border-[#EEE9E0] px-6 py-5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#B58A45]">
                  Votre commande
                </p>
                <h2 className="mt-1 font-display text-xl font-semibold text-[#211E19]">
                  Récapitulatif
                </h2>
              </div>

              <div className="space-y-5 p-6">
                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-[#777168]">Sous-total</span>
                  <span className="font-medium text-[#211E19]">
                    {formatPrice(total)}
                  </span>
                </div>

                <div className="flex items-center justify-between gap-4 text-sm">
                  <span className="text-[#777168]">Livraison</span>
                  <span className="font-semibold text-[#6D8A59]">
                    Gratuite
                  </span>
                </div>

                <div className="border-t border-dashed border-[#E8E2D8] pt-5">
                  <div className="flex items-end justify-between gap-4">
                    <span className="text-sm font-semibold text-[#211E19]">
                      Total
                    </span>
                    <span className="font-display text-2xl font-semibold text-[#B58A45]">
                      {formatPrice(total)}
                    </span>
                  </div>
                  <p className="mt-2 text-right text-[11px] text-[#8C857A]">
                    Taxes incluses si applicables
                  </p>
                </div>

                <div className="space-y-3 pt-2">
                  <Link
                    to="/checkout"
                    className="group flex min-h-12 w-full items-center justify-center gap-3 rounded-full bg-[#B58A45] px-5 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#B58A45]/20 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#967039] hover:shadow-xl"
                  >
                    Commander
                    <ArrowRight
                      size={17}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </Link>

                  <Link
                    to="/"
                    className="flex min-h-12 w-full items-center justify-center rounded-full border border-[#E8E2D8] px-5 py-3 text-sm font-semibold text-[#5F594F] transition-all duration-300 hover:border-[#B58A45] hover:bg-[#FAF6EE] hover:text-[#967039]"
                  >
                    Continuer mes achats
                  </Link>
                </div>

                <div className="flex items-start gap-3 border-t border-[#EEE9E0] pt-5">
                  <ShieldCheck
                    size={18}
                    className="mt-0.5 shrink-0 text-[#B58A45]"
                  />
                  <p className="text-xs leading-5 text-[#8C857A]">
                    Vos informations sont traitées de manière sécurisée lors de votre commande.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-[#8C857A]">
              <Truck size={15} />
              Livraison selon les conditions de la boutique
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
